import { create } from 'zustand';
import { ChatService } from '../services/ChatService';
import { chatLogger } from '../utils/loggerInstance';
import { ChatSessionItem, BackendChatMessage, IncrementalFormData } from '../types';
import { Industry } from '../types/industry';
import { UserProfile } from '../types/user';

import { DEFAULT_MODEL } from '../constants/ModelOptions';
import { ModelName } from '../constants/modelNames';
import { generateUuid } from '../utils/uuid';
import { resolveToModelName } from '../utils/resolveModelName';

// Initialize logger
const logger = chatLogger;

// Initialize service
const chatService = new ChatService();

// Constants
const MAX_SESSION_TITLE_LENGTH = 50;
const CACHE_DURATION = 30000; // 30 seconds cache
const PENDING_DELETION_TIMEOUT = 5 * 60 * 1000; // 5 minutes - assume deletion processed after this
const MAX_PENDING_DELETIONS = 100; // Safety limit to prevent unbounded growth

export interface ChatSessionState {
  // State
  currentSessionId: string | null;
  selectedModel: ModelName; // Track selected model in store
  selectedIndustry: Industry | null; // Track selected industry in store
  /** Document language for extraction/translation (e.g. from Translation dropdown). */
  documentLanguage: string;
  sessions: ChatSessionItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  offset: number;
  totalCount: number; // Optimization: Store total count here
  processingStates: Record<
    string,
    {
      isLoading: boolean;
      status: string | null;
      message: string | null;
      isFirstMessage?: boolean;
      model?: ModelName;
      industry?: Industry | null;
      data?: IncrementalFormData;
    }
  >;

  // Cache state (internal use)
  sessionsCache: {
    data: ChatSessionItem[];
    timestamp: number;
  } | null;
  loadingPromise: Promise<void> | null;

  // Track pending deletions to maintain accurate count
  // Map<sessionId, timestamp> - tracks when deletion was initiated for cleanup
  pendingDeletions: Map<string, number>;

  // Context state
  userId: string | null;
  workspaceId: string | null;

  // Persistence/Preferences state
  lastModel: ModelName | null;
  lastIndustry: Industry | null;

  // Actions
  setContext: (userId: string, workspaceId: string | null) => void;
  /** Creates a backend session and updates store state. Product analytics belong in the feature layer (see useChatSessionCreation). */
  createNewSession: (
    industry: Industry,
    model: ModelName,
    enabledProviders?: string[],
  ) => Promise<string>;
  switchToSession: (sessionId: string) => void;
  loadSessions: (
    limit?: number,
    forceRefresh?: boolean,

    mode?: 'replace' | 'append',
    searchQuery?: string,
  ) => Promise<void>;
  loadMoreSessions: (limit?: number, searchQuery?: string) => Promise<void>;
  refreshSessions: () => Promise<void>;
  addSessionToHistory: (
    sessionId: string,
    messages: BackendChatMessage[],
    industry: Industry | null,
    model?: ModelName,
  ) => void;
  deleteSession: (sessionId: string) => Promise<void>;
  clearCurrentSession: () => void;
  setProcessingState: (
    sessionId: string,
    state: {
      isLoading: boolean;
      status: string | null;
      message: string | null;
      optimisticMessage?: BackendChatMessage | null;
      isFirstMessage?: boolean;
      model?: ModelName;
      industry?: Industry | null;
      data?: IncrementalFormData;
    },
  ) => void;
  clearProcessingState: (sessionId: string) => void;
  setSelectedModel: (model: ModelName) => void;
  setSelectedIndustry: (industry: Industry | null) => void;
  setDocumentLanguage: (language: string) => void;
  setUserPreferences: (profile: UserProfile) => void;
  updateLastUsedPreferences: (model: ModelName, industry: Industry | null) => void;

  /** Reset store to initial state (e.g. on logout). Call from AuthContext only. */
  reset: () => void;
}

// Helper function to clean up pendingDeletions by removing:
// 1. Confirmed deleted sessions (not in server response)
// 2. Stale deletions (older than PENDING_DELETION_TIMEOUT)
// 3. Excess deletions (if exceeding MAX_PENDING_DELETIONS)
// This centralizes the logic and ensures consistency across code paths
const cleanupPendingDeletions = (
  currentPending: Map<string, number>,
  serverSessionIds: Set<string>,
  isDefinitiveResponse: boolean = false,
): Map<string, number> => {
  const now = Date.now();
  const updated = new Map<string, number>();

  // First, identify what to remove
  const confirmedDeleted: string[] = [];
  const staleDeleted: string[] = [];

  for (const [sessionId, timestamp] of currentPending.entries()) {
    // Only confirm as deleted if the response is definitive (e.g., page 1 refresh)
    // and the session is missing from the server's response.
    // Partial responses (like append/paginated loads) shouldn't be used to confirm deletion
    // unless the session was expected to be in that specific range.
    if (isDefinitiveResponse && !serverSessionIds.has(sessionId)) {
      confirmedDeleted.push(sessionId);
      continue;
    }

    // Remove if stale (older than timeout)
    if (now - timestamp > PENDING_DELETION_TIMEOUT) {
      staleDeleted.push(sessionId);
      continue;
    }

    // Keep this entry
    updated.set(sessionId, timestamp);
  }

  // If we have too many entries, remove oldest ones
  if (updated.size > MAX_PENDING_DELETIONS) {
    const sortedEntries = Array.from(updated.entries()).sort((a, b) => a[1] - b[1]); // Sort by timestamp
    const excessCount = updated.size - MAX_PENDING_DELETIONS;
    const excessDeleted = sortedEntries.slice(0, excessCount).map(([id]) => id);

    excessDeleted.forEach((id) => updated.delete(id));

    if (confirmedDeleted.length > 0 || staleDeleted.length > 0 || excessDeleted.length > 0) {
      logger.debug('Cleaned up pendingDeletions', {
        confirmedDeleted,
        staleDeleted,
        excessDeleted,
        remaining: updated.size,
      });
    }
  } else if (confirmedDeleted.length > 0 || staleDeleted.length > 0) {
    logger.debug('Cleaned up pendingDeletions', {
      confirmedDeleted,
      staleDeleted,
      remaining: updated.size,
    });
  }

  return updated;
};

// Helper: Merge and sort sessions with preservation logic
const mergeAndSortSessions = (
  currentSessions: ChatSessionItem[],
  newSessions: ChatSessionItem[],
  mode: 'replace' | 'append',
  shouldPreserveLocalSessions: boolean = true,
): ChatSessionItem[] => {
  // Sort new sessions by date (descending)
  newSessions.sort((a, b) => {
    const dateA = new Date(a.updated_at || a.created_at);
    const dateB = new Date(b.updated_at || b.created_at);
    return dateB.getTime() - dateA.getTime();
  });

  // SESSION PRESERVATION LOGIC
  // For replace mode, optionally preserve all current sessions that aren't in the backend response.
  // This is critical for sessions added via addSessionToHistory that haven't been persisted yet.
  // Pass false when results must match backend exactly (e.g., during search queries).
  if (mode === 'replace' && shouldPreserveLocalSessions) {
    // Find sessions in current state that are NOT in the new backend response
    const newSessionIds = new Set(newSessions.map((s) => s.id));
    const localOnlySessions = currentSessions.filter((s) => !newSessionIds.has(s.id));

    if (localOnlySessions.length > 0) {
      logger.debug('Preserving locally-added sessions not yet in backend response', {
        count: localOnlySessions.length,
        sessionIds: localOnlySessions.map((s) => s.id),
      });
      // Add local-only sessions to the beginning of the list
      newSessions.unshift(...localOnlySessions);
    }
  }

  // Combine sessions based on mode
  const allSessions = mode === 'replace' ? newSessions : [...currentSessions, ...newSessions];

  // Deduplicate sessions
  const uniqueSessions = Array.from(new Map(allSessions.map((s) => [s.id, s])).values());

  // Re-sort all sessions after combination
  return uniqueSessions.sort((a, b) => {
    const dateA = new Date(a.updated_at || a.created_at);
    const dateB = new Date(b.updated_at || b.created_at);
    return dateB.getTime() - dateA.getTime();
  });
};

// Helper: Calculate and apply session state updates
interface UpdateSessionStateParams {
  set: (partial: Partial<ChatSessionState>) => void;
  latestState: ChatSessionState;
  uniqueSessions: ChatSessionItem[];
  serverCount: number;
  newSessionsLength: number;
  mode: 'replace' | 'append';
  originalOffset: number;
  limit: number;
}

// Helper: Calculate and apply session state updates
const updateSessionState = ({
  set,
  latestState,
  uniqueSessions,
  serverCount,
  newSessionsLength,
  mode,
  originalOffset,
  limit,
}: UpdateSessionStateParams) => {
  const serverSessionIds = new Set(uniqueSessions.map((s) => s.id));

  // Only subtract pending deletions that are still in the server response
  const serverSideUnprocessedDeletions = Array.from(latestState.pendingDeletions.keys()).filter(
    (id) => serverSessionIds.has(id),
  );

  // Filter out pending deleted sessions from the UI list
  const filteredSessions = uniqueSessions.filter(
    (session) => !latestState.pendingDeletions.has(session.id),
  );

  // Clean up pendingDeletions
  // A 'replace' mode response (offset 0) is considered definitive for confirming deletions
  const isDefinitive = mode === 'replace' && originalOffset === 0;
  const updatedPendingDeletions = cleanupPendingDeletions(
    latestState.pendingDeletions,
    serverSessionIds,
    isDefinitive,
  );

  const hasMore = newSessionsLength === limit;
  const newOffset =
    mode === 'replace' ? filteredSessions.length : originalOffset + newSessionsLength;
  const adjustedCount = Math.max(0, serverCount - serverSideUnprocessedDeletions.length);

  set({
    sessions: filteredSessions,
    sessionsCache: { data: filteredSessions, timestamp: Date.now() },
    offset: newOffset,
    hasMore,
    totalCount: adjustedCount,
    pendingDeletions: updatedPendingDeletions,
  });

  logger.debug('Loaded and cached chat sessions', {
    mode,
    count: filteredSessions.length,
    newSessionsCount: newSessionsLength,
    offset: newOffset,
    hasMore,
  });
};

export const useChatStore = create<ChatSessionState>((set, get) => ({
  // Initial State
  currentSessionId: null,
  selectedModel: DEFAULT_MODEL,
  selectedIndustry: null,
  documentLanguage: 'English',
  sessions: [],
  isLoading: false,
  isLoadingMore: false,
  hasMore: true,
  offset: 0,
  totalCount: 0,
  processingStates: {},
  sessionsCache: null,
  loadingPromise: null,
  userId: null,
  workspaceId: null,
  pendingDeletions: new Map<string, number>(),
  lastModel: null,
  lastIndustry: null,

  // Actions

  setContext: (userId, workspaceId) => {
    const state = get();
    const hasUserChanged = state.userId !== userId;
    const hasWorkspaceChanged = state.workspaceId !== workspaceId;

    if (!hasUserChanged && !hasWorkspaceChanged) return;

    logger.debug('ChatStore: Context updated', {
      userId: userId ?? undefined,
      workspaceId: workspaceId ?? undefined,
    });

    // Update service
    chatService.setCurrentUser(userId ?? undefined);

    set({ userId, workspaceId });

    // Handle workspace change logic
    if (hasWorkspaceChanged) {
      if (workspaceId) {
        logger.info('Workspace changed, reloading sessions', { workspaceId });
        // Clear list immediately so UI doesn't show previous workspace's chats while loading.
        // Also prevents mergeAndSortSessions from preserving old sessions as "local only".
        set({
          sessions: [],
          sessionsCache: null,
          offset: 0,
          hasMore: true,
          totalCount: 0,
          currentSessionId: null,
          selectedModel: state.lastModel || DEFAULT_MODEL,
          selectedIndustry: state.lastIndustry || null,
          pendingDeletions: new Map<string, number>(), // Clear pending deletions on workspace change
          isLoading: true,
        });
        // Reload sessions
        get().loadSessions(10, true);
      } else {
        logger.info('No workspace selected, clearing sessions');
        set({
          sessions: [],
          sessionsCache: null,
          currentSessionId: null,
          selectedModel: DEFAULT_MODEL, // Reset model when no workspace
          selectedIndustry: null, // Clear industry when no workspace
          pendingDeletions: new Map<string, number>(), // Clear pending deletions
        });
      }
    } else if (hasUserChanged) {
      // User changed; null case is handled by clearAuthState() calling reset()
      // New user logged in: clear cache and pending deletions only; sessions will reload
      set({
        sessionsCache: null,
        pendingDeletions: new Map<string, number>(),
      });
    }
  },

  reset: () => {
    chatService.setCurrentUser(undefined);
    set({
      currentSessionId: null,
      selectedModel: DEFAULT_MODEL,
      selectedIndustry: null,
      documentLanguage: 'English',
      sessions: [],
      isLoading: false,
      isLoadingMore: false,
      hasMore: true,
      offset: 0,
      totalCount: 0,
      processingStates: {},
      sessionsCache: null,
      loadingPromise: null,
      userId: null,
      workspaceId: null,
      pendingDeletions: new Map<string, number>(),
      lastModel: null,
      lastIndustry: null,
    });
    logger.debug('Chat store reset (e.g. on logout)');
  },

  refreshSessions: async () => {
    return get().loadSessions(10, true);
  },

  loadMoreSessions: async (limit = 5, searchQuery?: string) => {
    const state = get();
    if (state.loadingPromise) return state.loadingPromise;
    if (!state.hasMore) return;

    return state.loadSessions(limit, false, 'append', searchQuery);
  },

  loadSessions: async (
    limit = 5,
    forceRefresh = false,
    mode: 'replace' | 'append' = 'replace',
    searchQuery?: string,
  ) => {
    const state = get();
    const { sessionsCache, loadingPromise, workspaceId } = state;

    // Check cache (only for replace mode, as append needs to fetch next page)
    // Skip cache when searching (search results should always be fresh)
    if (
      mode === 'replace' &&
      !forceRefresh &&
      !searchQuery && // Don't use cache when searching
      sessionsCache &&
      Date.now() - sessionsCache.timestamp < CACHE_DURATION
    ) {
      // Filter out pending deleted sessions from cache as well
      // This prevents deleted sessions from being restored from cache
      const currentState = get();
      const filteredCachedSessions = sessionsCache.data.filter(
        (session) => !currentState.pendingDeletions.has(session.id),
      );
      logger.debug('Using cached sessions (filtered)', {
        cachedCount: sessionsCache.data.length,
        filteredCount: filteredCachedSessions.length,
        pendingDeletions: currentState.pendingDeletions.size,
      });
      set({
        sessions: filteredCachedSessions,
        offset: filteredCachedSessions.length,
        hasMore: true,
      });
      return;
    }

    // Deduplication
    if (loadingPromise && !forceRefresh) {
      logger.debug('Session loading already in progress');
      return loadingPromise;
    }

    const loadPromise = (async () => {
      set({ [mode === 'replace' ? 'isLoading' : 'isLoadingMore']: true });

      try {
        const currentOffset = mode === 'replace' ? 0 : state.offset;
        logger.debug('Attempting to load chat sessions', {
          limit,
          mode,
          offset: currentOffset,
        });

        let response;
        if (workspaceId) {
          logger.debug('Loading workspace-scoped sessions', { workspaceId, searchQuery });
          response = await chatService.getWorkspaceSessions(
            workspaceId,
            limit,
            currentOffset,
            searchQuery,
          );
        } else {
          response = await chatService.getChatSessions(limit, currentOffset, searchQuery);
        }

        const newSessions = response?.sessions || [];

        // IMPORTANT: Re-fetch state to get the MOST RECENT sessions list
        // between the start of the async call and now. This prevents
        // optimistically deleted sessions from being re-added.
        const currentState = get();

        // Preserve local sessions only when not searching - search results should be exact backend matches
        const shouldPreserveLocalSessions = !searchQuery;
        const uniqueSessions = mergeAndSortSessions(
          currentState.sessions, // Use currentState instead of stale 'state'
          newSessions,
          mode,
          shouldPreserveLocalSessions,
        );

        const latestState = get();
        if (latestState.workspaceId === workspaceId) {
          updateSessionState({
            set,
            latestState,
            uniqueSessions,
            serverCount: response?.count || 0,
            newSessionsLength: newSessions.length,
            mode,
            originalOffset: currentOffset, // Use the actual offset used in the request
            limit,
          });
        }
      } catch (error) {
        logger.error('Failed to load chat sessions', error);
        if (get().workspaceId === workspaceId && mode === 'replace') {
          set({ sessions: [], hasMore: false });
        }
      } finally {
        if (get().workspaceId === workspaceId) {
          set({ isLoading: false, isLoadingMore: false, loadingPromise: null });
        }
      }
    })();

    set({ loadingPromise: loadPromise });
    return loadPromise;
  },

  createNewSession: async (industry: Industry, model: ModelName, enabledProviders?: string[]) => {
    const state = get();
    // Use empty string to track loading state for the session being created
    const NEW_SESSION_TEMP_ID = '';

    state.setProcessingState(NEW_SESSION_TEMP_ID, {
      isLoading: true,
      status: 'initializing',
      message: 'Initializing your secure chat session...',
    });

    try {
      const sessionId = await chatService.createNewChatSession(
        industry,
        'general',
        generateUuid(),
        model,
        state.workspaceId ?? '',
        enabledProviders,
      );

      // Transition processing state to the actual session ID
      state.setProcessingState(sessionId, {
        isLoading: true,
        status: 'session_created',
        message: 'Session created, preparing chat...',
      });

      // Clear the temporary processing state
      state.clearProcessingState(NEW_SESSION_TEMP_ID);

      set({
        currentSessionId: sessionId,
        selectedModel: model,
      });

      // Update preferences with the industry and model we sent
      get().updateLastUsedPreferences(model, industry);

      logger.info('Created new session', { sessionId });

      // Refresh list immediately
      get().loadSessions(10, true);

      return sessionId;
    } catch (error) {
      logger.error('Failed to create new session', error);
      state.clearProcessingState(NEW_SESSION_TEMP_ID);
      throw error;
    }
  },

  switchToSession: (sessionId) => {
    const state = get();
    const { sessions } = state;

    // Find session to get its model
    const session = sessions.find((s) => s.id === sessionId);

    // If session doesn't exist in current list or is pending deletion, don't switch to it
    // EXCEPT if it's currently being processed (e.g., first message in a new session)
    const isPendingDeletion = state.pendingDeletions.has(sessionId);
    const processingState = state.processingStates[sessionId];
    const isNewSessionInitializing = !!processingState;

    if ((!session && !isNewSessionInitializing) || isPendingDeletion) {
      logger.warn('Attempted to switch to session that does not exist or is pending deletion', {
        sessionId,
        isPendingDeletion,
        isNewSessionInitializing,
      });
      return;
    }

    // Determines the model to use:
    // 1. Session's specific model if available
    // 2. Fallback to processing state model if session is still initializing
    // 3. Fallback to DEFAULT_MODEL (via resolver)
    const modelToUse = resolveToModelName(
      session?.model ?? processingState?.model ?? DEFAULT_MODEL,
    );

    // Normalize industry
    const industryToUse = session?.industry || null || processingState?.industry || null;

    set({
      currentSessionId: sessionId,
      selectedModel: modelToUse,
      selectedIndustry: industryToUse,
    });
    chatService.setCurrentSession(sessionId, null);

    logger.info('Switched to session', {
      sessionId,
      model: modelToUse,
      industry: industryToUse,
    });
  },

  /* Redundant message actions removed */

  clearCurrentSession: () => {
    set({ currentSessionId: null });
    chatService.setCurrentSession('', null);
    logger.debug('Cleared current session in store');
  },

  addSessionToHistory: (sessionId, messages, industry: Industry | null, model) => {
    if (!sessionId || messages.length === 0) return;

    set((state) => {
      // Don't add session if it's pending deletion
      // This prevents deleted sessions from being restored
      if (state.pendingDeletions.has(sessionId)) {
        logger.debug('Skipping addSessionToHistory for pending deleted session', { sessionId });
        return {};
      }

      const firstUserMessage = messages.find((msg) => msg.role === 'user');
      let title = 'Chat';
      if (firstUserMessage?.content) {
        title =
          firstUserMessage.content.length > MAX_SESSION_TITLE_LENGTH
            ? firstUserMessage.content.substring(0, MAX_SESSION_TITLE_LENGTH) + '...'
            : firstUserMessage.content;
      }

      const sessionItem: ChatSessionItem = {
        id: sessionId,
        title: title,
        session_type: 'general',
        industry: industry,
        model: resolveToModelName(model ?? state.selectedModel ?? DEFAULT_MODEL),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const sessions = state.sessions;
      const existingIndex = sessions.findIndex((s) => s.id === sessionId);
      let updatedSessions;

      if (existingIndex >= 0) {
        const existingSession = sessions[existingIndex];
        const updatedSession = {
          ...sessionItem,
          created_at: existingSession.created_at,
          title: existingSession.title?.trim() ? existingSession.title : sessionItem.title,
          document_filename: existingSession.document_filename ?? sessionItem.document_filename,
        };
        updatedSessions = [...sessions];
        updatedSessions[existingIndex] = updatedSession;
      } else {
        updatedSessions = [sessionItem, ...sessions];
      }

      return {
        sessions: updatedSessions,
        sessionsCache: { data: updatedSessions, timestamp: Date.now() },
      };
    });
  },

  deleteSession: async (sessionId) => {
    try {
      logger.info('Deleting chat session', { sessionId });

      // Set processing state to deleting to show loading indicator in UI
      get().setProcessingState(sessionId, {
        isLoading: true,
        status: 'deleting',
        message: 'Deleting session...',
      });

      // Perform actual deletion FIRST (without optimistic UI update)
      // This ensures that any retries (e.g. CSRF 403) complete before we touch the state
      await chatService.deleteSession(sessionId);

      // Now update the state ONLY after successful backend deletion
      set((state) => {
        const wasCurrentSession = state.currentSessionId === sessionId;
        const newPendingDeletions = new Map(state.pendingDeletions);

        // Track as pending deletion to confirm it's gone from future server responses
        newPendingDeletions.set(sessionId, Date.now());

        const updatedSessions = state.sessions.filter((s) => s.id !== sessionId);

        const updatedCache = state.sessionsCache
          ? {
              data: state.sessionsCache.data.filter((s) => s.id !== sessionId),
              timestamp: state.sessionsCache.timestamp,
            }
          : null;

        const baseUpdate = {
          sessions: updatedSessions,
          sessionsCache: updatedCache,
          totalCount: Math.max(0, state.totalCount - 1),
          pendingDeletions: newPendingDeletions,
        };

        if (wasCurrentSession) {
          return {
            ...baseUpdate,
            currentSessionId: null,
            selectedModel: DEFAULT_MODEL,
            selectedIndustry: null,
          };
        }

        return baseUpdate;
      });

      // Clear processing state after successful deletion
      get().clearProcessingState(sessionId);

      logger.info('Chat session deleted successfully and removed from state', {
        sessionId,
      });
    } catch (error) {
      logger.error('Failed to delete chat session', error);
      // Clear processing state on error so UI can reset
      get().clearProcessingState(sessionId);
      // No need to restore state because we didn't update it optimistically
      throw error;
    }
  },

  // UI State
  setIsLoading: (isLoading: boolean) => set({ isLoading: isLoading }),

  setProcessingState: (sessionId, processingState) => {
    set((state) => ({
      processingStates: {
        ...state.processingStates,
        [sessionId]: {
          ...state.processingStates[sessionId],
          ...processingState,
        },
      },
    }));
  },

  clearProcessingState: (sessionId) => {
    set((state) => {
      const newStates = { ...state.processingStates };
      delete newStates[sessionId];
      return { processingStates: newStates };
    });
  },

  setSelectedModel: (model) => set({ selectedModel: model }),
  setSelectedIndustry: (industry: Industry | null) => set({ selectedIndustry: industry }),
  setDocumentLanguage: (language) => set({ documentLanguage: language }),
  setUserPreferences: (profile) => {
    const lastModelResolved =
      profile.last_model === undefined || profile.last_model === null
        ? null
        : resolveToModelName(profile.last_model);

    set({
      lastModel: lastModelResolved,
      lastIndustry: profile.last_industry,
    });

    // If no session is active and no manual selection has been made (or it's the default),
    // we can pre-populate the selection with these preferences
    const state = get();
    if (!state.currentSessionId) {
      if (lastModelResolved && (state.selectedModel === DEFAULT_MODEL || !state.selectedModel)) {
        set({ selectedModel: lastModelResolved });
      }
      if (profile.last_industry && !state.selectedIndustry) {
        set({ selectedIndustry: profile.last_industry });
      }
    }
  },
  updateLastUsedPreferences: (model, industry) => {
    set((state) => {
      const updates: Partial<ChatSessionState> = {};

      if (model !== state.lastModel) {
        updates.lastModel = model;
      }
      if (industry !== state.lastIndustry) {
        updates.lastIndustry = industry;
      }

      return Object.keys(updates).length > 0 ? updates : {};
    });
  },
}));
